import type { DragEvent } from 'react'
import clsx from 'clsx'
import { Badge } from './Badge'
import { Tile, TileContent, TileDescription, TileTitle } from './Tile'

/** Einheitliche Höhe im Bausteine-Icon-Grid (mit/ohne Statistik-Badges). */
const BAUSTEIN_TILE_MIN_H = 'min-h-48'

interface BausteinTileProps {
  name: string
  fach: string
  stufe: string
  jahr: string | number
  startDate?: string
  endDate?: string
  studentLabel?: string
  avgCompletionLabel?: string
  active?: boolean
  onClick?: () => void
  draggable?: boolean
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void
}

export function BausteinTile({
  name,
  fach,
  stufe,
  jahr,
  startDate,
  endDate,
  studentLabel,
  avgCompletionLabel,
  active = false,
  onClick,
  draggable = false,
  onDragStart
}: BausteinTileProps) {
  const dateLine =
    startDate || endDate ? `Start: ${startDate || '-'} - Ende: ${endDate || '-'}` : null

  return (
    <Tile
      variant="interactive"
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      className={clsx('h-full', BAUSTEIN_TILE_MIN_H, active && 'lr-baustein-active-tile')}
    >
      <TileContent className={clsx('flex h-full flex-col gap-1.5 p-4', BAUSTEIN_TILE_MIN_H)}>
        <div className="flex min-h-[2.75rem] items-start justify-between gap-2">
          <TileTitle className="line-clamp-2 flex-1 text-sm leading-snug">{name}</TileTitle>
          <div className="flex w-14 shrink-0 justify-end">
            {active ? <Badge variant="success">aktiv</Badge> : null}
          </div>
        </div>
        <TileDescription className="truncate">{fach}</TileDescription>
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Stufe {stufe} - {jahr}
        </p>
        <p
          className={clsx(
            'min-h-[2.5rem] text-xs leading-snug text-gray-500 dark:text-gray-400',
            dateLine ? 'line-clamp-2' : 'invisible'
          )}
          aria-hidden={!dateLine}
        >
          {dateLine ?? 'Start: – - Ende: –'}
        </p>
        <div className="mt-auto flex min-h-[1.75rem] flex-wrap items-center gap-1.5 pt-0.5">
          {studentLabel ? (
            <Badge variant="info" className="gap-0">
              Schüler: {studentLabel}
            </Badge>
          ) : null}
          {avgCompletionLabel ? (
            <Badge variant="success" className="gap-0">
              Ø: {avgCompletionLabel}
            </Badge>
          ) : null}
        </div>
      </TileContent>
    </Tile>
  )
}
